import React from 'react';
import { PlayerHeadshot } from '../../../shell/SleeperAvatar';
import { getKTCPickIdFromPick } from '../../../utils/ktcPickIds';

/**
 * TradeThumbnailCard — one trade option, shell-styled (ft-card white).
 * Sides are labeled from the viewer's perspective via `viewerRole`:
 * creatorItems come from the creator's roster, recipientItems from the
 * recipient's, so "You send" flips depending on who is looking.
 */

export const getVerdict = (n) => {
  if (n === 1) return { word: 'No way.', tone: 'hate' };
  if (n === 2) return { word: 'Hard pass.', tone: 'hate' };
  if (n === 3) return { word: 'Doubt it.', tone: 'hate' };
  if (n === 4) return { word: 'Tough sell.', tone: 'neutral' };
  if (n === 5) return { word: 'Maybe.', tone: 'neutral' };
  if (n === 6) return { word: 'Lean yes.', tone: 'neutral' };
  if (n === 7) return { word: 'Tempting.', tone: 'love' };
  if (n === 8) return { word: 'Likely.', tone: 'love' };
  if (n === 9) return { word: 'Almost done.', tone: 'love' };
  if (n === 10) return { word: 'Hell yes.', tone: 'love' };
  return { word: '', tone: 'neutral' };
};

const ordinalRound = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return `${n}`;
  const s = ['th', 'st', 'nd', 'rd'];
  const v = num % 100;
  return num + (s[(v - 20) % 10] || s[v] || s[0]);
};

const pickLabel = (pickData) =>
  pickData ? `${pickData.season} ${ordinalRound(pickData.round)}` : 'Draft pick';

const circleStyle = {
  width: 24, height: 24, borderRadius: '50%', background: 'var(--ft-surface-hover)', flex: 'none',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 9, fontWeight: 700, color: 'var(--ft-ink-2)',
};

const ItemRow = ({ item, playerData }) => {
  const rowStyle = {
    display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0',
    fontSize: 13.5, fontWeight: 500, minWidth: 0,
  };

  if (item.type === 'player') {
    const player = playerData?.[item.playerId];
    return (
      <li style={rowStyle}>
        {player?.sleeperId ? (
          <PlayerHeadshot playerId={player.sleeperId} size={24} />
        ) : player?.imageUrl ? (
          <img
            src={player.imageUrl}
            alt=""
            style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover', flex: 'none', background: 'var(--ft-surface-hover)' }}
          />
        ) : (
          <span style={circleStyle}>{(player?.position || '?').slice(0, 2).toUpperCase()}</span>
        )}
        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {player?.name || 'Unknown player'}
          {player?.position ? (
            <span style={{ color: 'var(--ft-ink-3)', fontWeight: 400, fontSize: 11.5, marginLeft: 5 }}>
              {player.position} · {player.team || 'FA'}
            </span>
          ) : null}
        </span>
      </li>
    );
  }

  return (
    <li style={rowStyle}>
      <span style={circleStyle}>{item.pickData?.round ? `R${item.pickData.round}` : 'PK'}</span>
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {pickLabel(item.pickData)}
      </span>
    </li>
  );
};

const MAX_ITEMS_SHOWN = 4;

const ItemColumn = ({ label, items, playerData }) => {
  const shown = items.slice(0, MAX_ITEMS_SHOWN);
  const rest = items.length - shown.length;
  return (
    <div style={{ minWidth: 0 }}>
      <span className="ft-k" style={{ fontSize: 10 }}>{label}</span>
      <ul style={{ listStyle: 'none', margin: '6px 0 0', padding: 0 }}>
        {shown.map((item, idx) => (
          <ItemRow key={item.id || idx} item={item} playerData={playerData} />
        ))}
        {items.length === 0 ? (
          <li style={{ fontSize: 13, color: 'var(--ft-ink-3)', padding: '3px 0' }}>Nothing</li>
        ) : null}
        {rest > 0 ? (
          <li style={{ fontSize: 12, color: 'var(--ft-ink-3)', padding: '3px 0' }}>+{rest} more</li>
        ) : null}
      </ul>
    </div>
  );
};

const TradeThumbnailCard = ({ trade, playerData, league, index, viewerRole = 'recipient' }) => {
  const generateKTCLink = () => {
    // Get player IDs for team giving
    const teamGivePlayerIds = (trade.recipientItems || [])
      .filter(item => item.type === 'player' && item.playerId)
      .map(item => playerData[item.playerId]?.ktcId)
      .filter(Boolean);

    // Get pick IDs for team giving
    const teamGivePickIds = (trade.recipientItems || [])
      .filter(item => item.type === 'pick' && item.pickData)
      .map(item => getKTCPickIdFromPick(item.pickData))
      .filter(Boolean);

    const teamGiveIds = [...teamGivePlayerIds, ...teamGivePickIds];

    // Get player IDs for team receiving
    const teamGetPlayerIds = (trade.creatorItems || [])
      .filter(item => item.type === 'player' && item.playerId)
      .map(item => playerData[item.playerId]?.ktcId)
      .filter(Boolean);

    // Get pick IDs for team receiving
    const teamGetPickIds = (trade.creatorItems || [])
      .filter(item => item.type === 'pick' && item.pickData)
      .map(item => getKTCPickIdFromPick(item.pickData))
      .filter(Boolean);

    const teamGetIds = [...teamGetPlayerIds, ...teamGetPickIds];

    // Only generate link if both sides have items
    if (teamGiveIds.length === 0 || teamGetIds.length === 0) {
      return null;
    }

    // Dynamic format based on league settings
    const format = league?.isSuperFlex ? '2' : '1';

    // Dynamic TEP based on league settings
    // 0 -> tep=0, 0.5 -> tep=1, 1.0 -> tep=2, 1.5 -> tep=3
    const tepValue = league?.tePremium || 0;
    const tepMap = { 0: '0', 0.5: '1', 1: '2', 1.5: '3' };
    const tep = tepMap[tepValue] || '0';

    return `https://keeptradecut.com/trade-calculator?var=5&pickVal=0&teamOne=${teamGiveIds.join('|')}&teamTwo=${teamGetIds.join('|')}&format=${format}&isStartup=0&tep=${tep}`;
  };

  const youSend = viewerRole === 'creator' ? trade.creatorItems || [] : trade.recipientItems || [];
  const youGet = viewerRole === 'creator' ? trade.recipientItems || [] : trade.creatorItems || [];
  const grade = trade.grade;
  const verdict = grade ? getVerdict(grade.value) : null;
  const ktcLink = generateKTCLink();

  return (
    <div className="ft-card white" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
        <span className="ft-k">Option {(index ?? 0) + 1}</span>
        {grade ? <span className="ft-chip dark">{grade.value}/10</span> : null}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <ItemColumn label="You get" items={youGet} playerData={playerData} />
        <ItemColumn label="You send" items={youSend} playerData={playerData} />
      </div>

      {grade && verdict ? (
        <div style={{ borderTop: '1px solid var(--ft-hairline)', paddingTop: 12 }}>
          <div
            style={
              verdict.tone === 'hate'
                ? { borderLeft: '3px solid var(--ft-red)', paddingLeft: 10 }
                : verdict.tone === 'love'
                  ? { borderLeft: '3px solid var(--ft-green)', paddingLeft: 10 }
                  : undefined
            }
          >
            <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em' }}>{verdict.word}</span>
            {Array.isArray(grade.reasons) && grade.reasons.length > 0 ? (
              <div className="ft-pillrow" style={{ marginTop: 8 }}>
                {grade.reasons.map((r) => (
                  <span key={r.id || r.label} className="ft-chip outline">{r.label}</span>
                ))}
              </div>
            ) : null}
            {grade.notes ? (
              <p style={{ margin: '8px 0 0', fontSize: 13.5, color: 'var(--ft-ink-2)', fontStyle: 'italic', lineHeight: 1.45 }}>
                “{grade.notes}”
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      {ktcLink ? (
        <a
          href={ktcLink}
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--ft-ink-3)', textDecoration: 'none' }}
        >
          Check it on KeepTradeCut →
        </a>
      ) : null}
    </div>
  );
};

export default TradeThumbnailCard;
